// Loads the agent's dialer config, then drives the Telnyx WebRTC call.
// Scope: numbers purchased through our app (they live on our Telnyx account).

import { useState, useEffect, useCallback, useMemo } from "react";
import useTelnyxDialer from "./useTelnyxDialer";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

const authToken = () => localStorage.getItem("token") || "";

const formatDuration = (seconds) => {
  const m = Math.floor(seconds / 60).toString().padStart(2, "0");
  const s = (seconds % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
};

export function useDialer({ agentId }) {
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);
  // Which of the agent's numbers to show as caller ID (agent can own several).
  const [selectedCallerId, setSelectedCallerId] = useState(null);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    setConfigError(null);
    try {
      const url = `${API_BASE}/dialer/config${agentId ? `?agentId=${encodeURIComponent(agentId)}` : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${authToken()}` } });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.message || "Failed to load dialer config");
      setConfig(data);
      // Keep the user's pick if the server still offers it.
      setSelectedCallerId((prev) =>
        prev && (data?.callerIds || []).includes(prev) ? prev : data?.callerId || null
      );
    } catch (err) {
      setConfigError(err.message || "Failed to load dialer config");
      setConfig(null);
    } finally {
      setConfigLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const callerIds = config?.callerIds?.length
    ? config.callerIds
    : [config?.callerId].filter(Boolean);
  const callerId = selectedCallerId || config?.callerId || null;

  const telnyx = useTelnyxDialer({
    enabled: !!config?.ready,
    agentId,
    callerId,
    callerName: config?.callerName,
    initialAuth: config?.auth || null,
  });

  const formattedDuration = useMemo(
    () => formatDuration(telnyx.callDuration || 0),
    [telnyx.callDuration]
  );

  return {
    ...telnyx,
    formattedDuration,
    config,
    configLoading,
    configError,
    provider: config?.provider || null,
    callerId,
    callerIds,
    setCallerId: setSelectedCallerId,
    reloadConfig: loadConfig,
    // Nothing to connect to until a number is resolved.
    blocked: !configLoading && !config?.ready,
    blockedMessage: config?.message || configError || null,
  };
}

export default useDialer;
